import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Bot, CheckCircle2, User } from 'lucide-react';
import { AGENT_STATUSES, type CreateColleagueRequest } from '@command-room/shared';
import { useCreateColleague, useDepartments, useImportSpecText } from '@/api/queries';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';

type ColleagueType = 'AI' | 'Human';

interface FormValues {
  name: string;
  role: string;
  department: string;
  status: string;
  slack_handle: string;
  slack_user_id: string;
  webhook_url: string;
  agent_folder_id: string;
  /** Aaron's job description, pasted as XML or with [Section] headings. AI only. */
  spec_text: string;
}

const EMPTY: FormValues = {
  name: '',
  role: '',
  department: '',
  status: '',
  slack_handle: '',
  slack_user_id: '',
  webhook_url: '',
  agent_folder_id: '',
  spec_text: '',
};

interface NewColleagueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewColleagueDialog({ open, onOpenChange }: NewColleagueDialogProps) {
  const navigate = useNavigate();
  const { data: departments } = useDepartments();
  const create = useCreateColleague();

  const [type, setType] = useState<ColleagueType>('AI');
  const importSpec = useImportSpecText();
  const [importError, setImportError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  const isAI = type === 'AI';
  const department = watch('department');
  const status = watch('status');
  const defaultStatus = isAI ? 'onboarding' : 'active';

  function close(next: boolean) {
    if (!next) {
      reset(EMPTY);
      setType('AI');
      setImportError(null);
      create.reset();
      importSpec.reset();
    }
    onOpenChange(next);
  }

  async function onSubmit(values: FormValues) {
    setImportError(null);
    const name = values.name.trim();

    const body: CreateColleagueRequest = {
      name,
      type,
      role: values.role.trim() || null,
      department: values.department || null,
      status: values.status || defaultStatus,
      slack_handle: values.slack_handle.trim() || null,
      slack_user_id: values.slack_user_id.trim() || null,
      ...(isAI
        ? {
            webhook_url: values.webhook_url.trim() || null,
            agent_folder_id: values.agent_folder_id.trim() || null,
          }
        : {}),
    };

    await create.mutateAsync(body);

    // The colleague exists now — a failed spec import must not read as a failed create.
    const specText = isAI ? values.spec_text.trim() : '';
    if (specText) {
      try {
        await importSpec.mutateAsync({ name, text: specText });
      } catch (err) {
        setImportError(
          `${name} was created, but the job description could not be imported: ${(err as Error).message}`,
        );
        return;
      }
    }

    close(false);
    navigate(`/agents/${encodeURIComponent(name)}`);
  }

  const isPending = create.isPending || importSpec.isPending;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title="New colleague"
        description="Add an AI agent or a human team member to the registry."
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {/* Type picker */}
          <div className="flex flex-col gap-1.5">
            <Label>Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {(['AI', 'Human'] as const).map((t) => {
                const Icon = t === 'AI' ? Bot : User;
                const selected = type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    aria-pressed={selected}
                    className={cn(
                      'flex items-center gap-2.5 rounded-md border p-3 text-left text-sm transition-colors',
                      selected
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    <Icon className={cn('h-4 w-4 shrink-0', selected && 'text-primary')} />
                    <span>
                      <span className="block font-medium">{t === 'AI' ? 'AI agent' : 'Human'}</span>
                      <span className="block text-xs text-muted-foreground">
                        {t === 'AI' ? 'n8n workflow, spec, skills' : 'Team member — no spec or skills'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="colleague-name">Name *</Label>
              <Input
                id="colleague-name"
                {...register('name', { required: true, maxLength: 100 })}
                className={errors.name ? 'border-destructive' : ''}
                placeholder={isAI ? 'e.g. Clara' : 'e.g. Alex Keller'}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="colleague-role">Role</Label>
              <Input
                id="colleague-role"
                {...register('role')}
                placeholder={isAI ? 'e.g. Social media writer' : 'e.g. Head of Operations'}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Department</Label>
              <Select
                value={department || '__none__'}
                onValueChange={(v) => setValue('department', v === '__none__' ? '' : v)}
              >
                <SelectItem value="__none__">— unassigned —</SelectItem>
                {departments?.map((d) => (
                  <SelectItem key={d.id} value={d.name}>
                    {d.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select value={status || defaultStatus} onValueChange={(v) => setValue('status', v)}>
                {AGENT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="colleague-slack-handle">Slack handle</Label>
              <Input id="colleague-slack-handle" {...register('slack_handle')} placeholder="@clara" />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="colleague-slack-id">Slack user ID</Label>
              <Input
                id="colleague-slack-id"
                {...register('slack_user_id')}
                className="font-mono text-xs"
                placeholder="U01ABCDEF"
              />
            </div>

            {isAI && (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="colleague-webhook">Webhook URL</Label>
                  <Input
                    id="colleague-webhook"
                    {...register('webhook_url')}
                    className="font-mono text-xs"
                    placeholder="https://n8n…/webhook/…"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="colleague-folder">Drive folder ID</Label>
                  <Input
                    id="colleague-folder"
                    {...register('agent_folder_id')}
                    className="font-mono text-xs"
                    placeholder="1Cg9qpobnZ…"
                  />
                </div>
              </>
            )}
          </div>

          {isAI && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="colleague-spec">Job description (optional)</Label>
              <Textarea
                id="colleague-spec"
                rows={8}
                {...register('spec_text')}
                className="resize-y font-mono text-xs"
                placeholder={'Paste Aaron\'s job description here — either XML (<Role>…</Role>) or bracket headings ([Role]).'}
              />
              <p className="text-xs text-muted-foreground">
                Recognised sections: Personality, Role, NoGos, Tasks, SkillsAndAuthorities,
                ImportantNotes, CommunicationStyleOverrides. Organization and communication style
                are injected automatically — leave them out.
              </p>
            </div>
          )}

          {importSpec.isSuccess && importSpec.data && (
            <p className="flex items-center gap-1 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" />
              Imported {Object.keys(importSpec.data.sections).length} sections (
              {importSpec.data.format === 'xml' ? 'XML' : 'bracket headings'}).
            </p>
          )}

          {(create.error || importError) && (
            <p className="flex items-start gap-1 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {importError ?? (create.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => close(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {create.isPending
                ? 'Creating…'
                : importSpec.isPending
                ? 'Importing…'
                : `Create ${isAI ? 'agent' : 'colleague'}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
